/**
 * Maps Clerk Billing subscription / has({ plan }) to OPTRA tiers used by tierAccess.js.
 * Configure Clerk plan slugs in the dashboard, then optionally set env overrides:
 * VITE_CLERK_BILLING_PLAN_SCALE, VITE_CLERK_BILLING_PLAN_ENTERPRISE, VITE_CLERK_BILLING_PLAN_ACCELERATE
 */

const getEnv = (key) => {
  try {
    return typeof import.meta !== "undefined" ? (import.meta.env[key] || "").trim() : "";
  } catch {
    return "";
  }
};

function getHostname() {
  try {
    return typeof window !== "undefined" ? (window.location?.hostname || "").toLowerCase() : "";
  } catch {
    return "";
  }
}

function isPreviewHost(hostname) {
  if (!hostname) return false;
  return hostname.endsWith(".vercel.app") || hostname.endsWith(".onrender.com");
}

export function getClerkPublishableKey() {
  const prodKey = getEnv("VITE_CLERK_PUBLISHABLE_KEY");
  const devKey = getEnv("VITE_CLERK_DEV_PUBLISHABLE_KEY");
  const previewKey = getEnv("VITE_CLERK_PREVIEW_PUBLISHABLE_KEY");
  const forceProdInDev = ["1", "true", "yes"].includes(
    getEnv("VITE_CLERK_FORCE_PROD_KEY_IN_DEV").toLowerCase()
  );

  let isDev = false;
  try {
    isDev = Boolean(import.meta.env?.DEV);
  } catch {
    isDev = false;
  }

  // In local dev, prefer explicit dev key. Avoid auto-using prod key
  // because custom-domain Clerk setups commonly fail with 400 locally.
  if (isDev && !forceProdInDev) {
    return devKey || "";
  }

  // Preview deployments (for example *.vercel.app) frequently use different
  // Clerk instances/domains than production custom domains.
  const hostname = getHostname();
  if (isPreviewHost(hostname) && !forceProdInDev) {
    return previewKey || devKey || prodKey || "";
  }

  return prodKey || devKey || "";
}

function planKeysForTier(tier) {
  const envKey =
    tier === "Scale"
      ? "VITE_CLERK_BILLING_PLAN_SCALE"
      : tier === "Enterprise"
        ? "VITE_CLERK_BILLING_PLAN_ENTERPRISE"
        : "VITE_CLERK_BILLING_PLAN_ACCELERATE";
  const override = getEnv(envKey);
  const slug = tier.toLowerCase();
  const keys = new Set();
  if (override) keys.add(override);
  keys.add(slug);
  keys.add(tier);
  return [...keys];
}

/**
 * @param {(arg: { plan: string }) => boolean} has - from useAuth()
 * @param {unknown} subscription - from useSubscription().data
 * @returns {"Scale"|"Enterprise"|"Accelerate"|null}
 */
export function resolveTierFromClerk(has, subscription) {
  if (typeof has !== "function") return null;

  const tryTier = (tier) => {
    for (const key of planKeysForTier(tier)) {
      try {
        if (key && has({ plan: key })) return tier;
      } catch {
        /* Clerk throws if plan slug unknown to dashboard types */
      }
    }
    return null;
  };

  const fromHas =
    tryTier("Accelerate") || tryTier("Enterprise") || tryTier("Scale");
  if (fromHas) return fromHas;

  if (!subscription) return null;

  let blob = "";
  try {
    blob = JSON.stringify(subscription).toLowerCase();
  } catch {
    return null;
  }

  if (blob.includes("accelerate")) return "Accelerate";
  if (blob.includes("enterprise")) return "Enterprise";
  if (blob.includes("scale")) return "Scale";

  return null;
}

export function isClerkBillingSyncEnabled() {
  try {
    const v = import.meta.env?.VITE_CLERK_BILLING_ENABLED;
    if (v === "false" || v === "0") return false;
    return v === "true" || v === "1";
  } catch {
    return false;
  }
}

/** True when the SPA has a Clerk publishable key (sign-in + optional Billing UI). */
export function isClerkConfigured() {
  try {
    const pk = getClerkPublishableKey();
    return pk.length > 0;
  } catch {
    return false;
  }
}
